import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixLoyaltyReferralRewardV1ReferralRewardsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/referral-rewards',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/referral-rewards',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/referral-rewards',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/referral-rewards',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/referral-rewards',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/referral-rewards',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/referral-rewards',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/referral-rewards',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/referral-rewards',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_referral_rewards';

/** Retrieves a referral reward. */
export function getReferralReward(payload: object): RequestOptionsFactory<any> {
  function __getReferralReward({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.referral_reward',
      method: 'GET' as any,
      methodFqn:
        'wix.loyalty.referral.reward.v1.ReferralRewards.GetReferralReward',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralRewardV1ReferralRewardsUrl({
        protoPath: '/v1/referral-rewards/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referralReward.createdDate' },
              { path: 'referralReward.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'referralReward.coupon.couponSpecification.minimumSubtotal',
              },
              {
                path: 'referralReward.coupon.couponSpecification.fixedAmountOptions.amount',
              },
              {
                path: 'referralReward.coupon.couponSpecification.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getReferralReward;
}

/**
 * Creates a query to retrieve a list of referral rewards.
 *
 * The `queryReferralRewards()` function builds a query to retrieve a list of events and returns a `ReferralRewardsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `ReferralRewardsQueryBuilder` functions onto the query. `ReferralRewardsQueryBuilder` functions enable you to sort, filter, and control the results `queryReferralRewards()` returns.
 *
 * `queryReferralRewards()` runs with these `ReferralRewardsQueryBuilder` defaults, which you can override:
 *
 * - `limit(50)`
 * - `descending("_createdDate")`
 *
 * The functions that are chained to `queryReferralRewards()` are applied in the order they're called. For example, if you apply ascending('rewardedReferredFriendId') and then descending('rewardType'), the results are sorted first by the referred friend ID, and then, if there are multiple results with the same ID, the items are sorted by reward type.
 */
export function queryReferralRewards(
  payload: object
): RequestOptionsFactory<any> {
  function __queryReferralRewards({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.referral_reward',
      method: 'POST' as any,
      methodFqn:
        'wix.loyalty.referral.reward.v1.ReferralRewards.QueryReferralRewards',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralRewardV1ReferralRewardsUrl({
        protoPath: '/v1/referral-rewards/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referralRewards.createdDate' },
              { path: 'referralRewards.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'referralRewards.coupon.couponSpecification.minimumSubtotal',
              },
              {
                path: 'referralRewards.coupon.couponSpecification.fixedAmountOptions.amount',
              },
              {
                path: 'referralRewards.coupon.couponSpecification.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryReferralRewards;
}

/**
 * Retrieves rewards from all metasites that the caller is the member of.
 *
 * This method must be called with a [user identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users).
 */
export function bulkGetReferralRewards(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkGetReferralRewards({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.referral_reward',
      method: 'GET' as any,
      methodFqn:
        'wix.loyalty.referral.reward.v1.ReferralRewards.BulkGetReferralRewards',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralRewardV1ReferralRewardsUrl({
        protoPath: '/v1/bulk/referral-rewards',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rewardsInSite.rewards.createdDate' },
              { path: 'rewardsInSite.rewards.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'rewardsInSite.rewards.coupon.couponSpecification.minimumSubtotal',
              },
              {
                path: 'rewardsInSite.rewards.coupon.couponSpecification.fixedAmountOptions.amount',
              },
              {
                path: 'rewardsInSite.rewards.coupon.couponSpecification.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkGetReferralRewards;
}
